// frontend/src/services/soporteService.js
import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
// Strip /api/content if present to get clean base URL for soporte endpoints
const CLEAN_BASE_URL = BASE_URL.replace('/api/content', '');
const SOPORTE_API_URL = `${CLEAN_BASE_URL}/api/soporte`;

const soporteApi = axios.create({
  baseURL: SOPORTE_API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Obtener CSRF token
function getCookie(name) {
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === (name + '=')) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}

// Interceptor para agregar JWT token
soporteApi.interceptors.request.use((config) => {
  // Agregar JWT token si existe
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }

  // Agregar CSRF token para métodos que modifican datos
  if (['post', 'put', 'patch', 'delete'].includes(config.method)) {
    config.headers['X-CSRFToken'] = getCookie('csrftoken');
  }

  return config;
});

// Interceptor para manejar errores
soporteApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      console.log('No autenticado - redirigir a login');
      // Opcional: redirigir automáticamente
      // window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

const soporteService = {
  /**
   * Obtiene los slots disponibles para agendar soporte
   * @param {string} startDate - Fecha de inicio (formato YYYY-MM-DD)
   * @param {number} days - Días a futuro (default: 14)
   * @returns {Promise} - Promesa con los slots disponibles
   */
  getSlots: async (startDate = null, days = 14) => {
    const params = { days };
    if (startDate) {
      params.start_date = startDate;
    }
    return soporteApi.get('/reservations/slots/', { params });
  },

  /**
   * Crea una nueva reserva de soporte
   * @param {Object} data - Datos de la reserva
   * @param {string} data.start_time - Fecha y hora de inicio (ISO format)
   * @param {string} data.name - Nombre del contacto
   * @param {string} data.email - Email del contacto
   * @param {string} [data.phone] - Teléfono (opcional)
   * @param {string} [data.company] - Empresa (opcional)
   * @param {string} [data.notes] - Notas adicionales (opcional)
   * @returns {Promise} - Promesa con los datos de la reserva creada
   */
  createReservation: async (data) => {
    return soporteApi.post('/reservations/', data);
  },

  /**
   * Obtiene la lista de reservas
   * @param {Object} params - Parámetros de filtrado (status, email, etc.)
   * @returns {Promise} - Promesa con la lista de reservas
   */
  getReservations: async (params = {}) => {
    return soporteApi.get('/reservations/', { params });
  },

  /**
   * Obtiene el detalle de una reserva
   * @param {number} id - ID de la reserva
   * @returns {Promise} - Promesa con los datos de la reserva
   */
  getReservation: async (id) => {
    return soporteApi.get(`/reservations/${id}/`);
  },

  /**
   * Actualiza una reserva
   * @param {number} id - ID de la reserva
   * @param {Object} data - Datos a actualizar
   * @returns {Promise} - Promesa con los datos actualizados
   */
  updateReservation: async (id, data) => {
    return soporteApi.patch(`/reservations/${id}/`, data);
  },

  /**
   * Cancela una reserva
   * @param {number} id - ID de la reserva
   * @param {string} [reason] - Razón de cancelación (opcional)
   * @returns {Promise} - Promesa confirmando la cancelación
   */
  cancelReservation: async (id, reason = null) => {
    const data = reason ? { reason } : {};
    return soporteApi.post(`/reservations/${id}/cancel/`, data);
  },

  /**
   * Marca una reserva como completada
   * @param {number} id - ID de la reserva
   * @returns {Promise} - Promesa confirmando la actualización
   */
  completeReservation: async (id) => {
    return soporteApi.post(`/reservations/${id}/complete/`);
  },

  /**
   * Marca una reserva como "no asistió"
   * @param {number} id - ID de la reserva
   * @returns {Promise} - Promesa confirmando la actualización
   */
  markNoShow: async (id) => {
    return soporteApi.post(`/reservations/${id}/mark_no_show/`);
  },

  /**
   * Elimina una reserva
   * @param {number} id - ID de la reserva
   * @returns {Promise} - Promesa confirmando la eliminación
   */
  deleteReservation: async (id) => {
    return soporteApi.delete(`/reservations/${id}/`);
  },
};

export default soporteService;
